import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import SmartUnitSelector, { SIZE_UNITS } from '../../SmartUnitSelector/SmartUnitSelector';
import DataTable from '../DataTable/DataTable';

const API = process.env.REACT_APP_API_URL;

const KM = {
  orange: '#F15A24', orangeLight: '#FEF0EB',
  blue: '#1A3A6B', green: '#39B54A',
  teal: '#00B4D8', border: '#E5E7EB',
  text: '#1A1A2E', muted: '#6B7280', bg: '#F9FAFB',
};

const STOCK_COLOR = { high: '#39B54A', mid: '#F59E0B', low: '#EF4444' };
function stockColor(qty) {
  if (qty > 200) return STOCK_COLOR.high;
  if (qty > 50) return STOCK_COLOR.mid;
  return STOCK_COLOR.low;
}

const formCard = { background: '#fff', border: `1px solid ${KM.border}`, borderRadius: 12, overflow: 'hidden', marginBottom: 20 };
const formHeader = { background: KM.blue, padding: '16px 24px', display: 'flex', alignItems: 'center', gap: 10 };
const headerIcon = { width: 32, height: 32, background: 'rgba(255,255,255,0.15)', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 15, fontWeight: 600 };
const fieldStyle = { display: 'flex', flexDirection: 'column', gap: 5 };
const labelStyle = { fontSize: 11, fontWeight: 500, color: KM.muted, textTransform: 'uppercase', letterSpacing: '0.05em' };
const inputStyle = { padding: '9px 12px', border: `1px solid ${KM.border}`, borderRadius: 8, fontSize: 13, color: KM.text, background: '#fff', fontFamily: 'inherit', outline: 'none' };
const submitBtn = { gridColumn: 'span 2', padding: 11, background: KM.orange, color: '#fff', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit', marginTop: 4 };

export default function Products({ showToast }) {
  const location = useLocation();
  const [rows, setRows] = useState([]);
  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    productName: '', categoryId: '', subCategoryId: '', brandId: '',
    isNewArrival: false, isHotDeal: false, isRecommended: false,
  });
  const [variants, setVariants] = useState([{ variantName: '', unit: '', mrp: '', salesPrice: '', stock: '' }]);

  useEffect(() => {
    fetchCategories();
    fetchSubCategories();
    fetchBrands();
    fetchProducts();
  }, [location.search]);

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/products`);
      const data = await res.json();
      setRows(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error(err);
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const r = await fetch(`${API}/categories`);
      setCategories(await r.json());
    } catch { console.log('Category error'); }
  };

  const fetchSubCategories = async () => {
    try {
      const r = await fetch(`${API}/subcategories`);
      setSubCategories(await r.json());
    } catch { console.log('Sub-Category error'); }
  };

  const fetchBrands = async () => {
    try {
      const r = await fetch(`${API}/brands`);
      const data = await r.json();
      setBrands(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Brand fetch error:', err);
    }
  };

  const filteredSubCategories = subCategories.filter(sc =>
    String(sc.categoryId) === String(formData.categoryId)
  );

  const addVariantField = () => setVariants([...variants, { variantName: '', unit: '', mrp: '', salesPrice: '', stock: '' }]);
  const removeVariantField = (i) => { const l = [...variants]; l.splice(i, 1); setVariants(l); };
  const updateVariant = (i, field, value) => { const v = [...variants]; v[i][field] = value; setVariants(v); };

  const resetForm = () => {
    setEditingId(null); setShowForm(false);
    setFormData({ productName: '', categoryId: '', subCategoryId: '', brandId: '', isNewArrival: false, isHotDeal: false, isRecommended: false });
    setVariants([{ variantName: '', unit: '', mrp: '', salesPrice: '', stock: '' }]);
    setImageFile(null);
  };

  const handleEditClick = (product) => {
    setEditingId(product.id);
    setFormData({
      productName: product.productName,
      categoryId: product.categoryId || '',
      subCategoryId: product.subCategoryId || '',
      brandId: product.brandId || '',
      isNewArrival: !!product.isNewArrival,
      isHotDeal: !!product.isHotDeal,
      isRecommended: !!product.isRecommended,
    });
    setVariants(product.Variants?.length ? product.Variants : [{ variantName: '', unit: '', mrp: '', salesPrice: '', stock: '' }]);
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const data = new FormData();
    Object.keys(formData).forEach(key => data.append(key, formData[key]));
    if (imageFile) data.append('image', imageFile);
    data.append('variants', JSON.stringify(variants));
    const method = editingId ? 'PUT' : 'POST';
    const url = editingId ? `${API}/products/update/${editingId}` : `${API}/products/add`;
    try {
      const res = await fetch(url, { method, body: data });
      if (res.ok) { showToast(editingId ? 'Updated!' : 'Added!', editingId ? 'Product updated successfully.' : 'New product added.'); resetForm(); fetchProducts(); }
    } catch { showToast('Server Error', 'Something went wrong. Try again.'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this product?')) return;
    try {
      const res = await fetch(`${API}/products/${id}`, { method: 'DELETE' });
      if (res.ok) { setRows(prev => prev.filter(r => r.id !== id)); showToast('Deleted!', 'Product removed successfully.'); }
    } catch { showToast('Error', 'Could not complete the action.'); }
  };

  return (
    <div>
      <div className="section-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div className="section-title">Products Management</div>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button className="action-btn btn-edit" onClick={() => (showForm ? resetForm() : setShowForm(true))}>
            {showForm ? 'Close' : '+ Add Product'}
          </button>
        </div>
      </div>

      {showForm && (
        <div style={formCard}>
          <div style={formHeader}>
            <div style={headerIcon}>P</div>
            <div>
              <div style={{ fontSize: 15, fontWeight: 500, color: '#fff' }}>{editingId ? 'Edit Product' : 'Add New Product'}</div>
              <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.65)', marginTop: 1 }}>Manage details and inventory variants</div>
            </div>
          </div>

          <div style={{ padding: '20px 24px 24px' }}>
            <form onSubmit={handleSubmit} style={{ display: 'grid', gap: 14, gridTemplateColumns: '1fr 1fr' }}>

              <div style={{ ...fieldStyle, gridColumn: 'span 2' }}>
                <label style={labelStyle}>Product Name</label>
                <input style={inputStyle} type="text" required value={formData.productName}
                  onChange={e => setFormData({ ...formData, productName: e.target.value })} />
              </div>

              <div style={fieldStyle}>
                <label style={labelStyle}>Category</label>
                <select style={inputStyle} required value={formData.categoryId}
                  onChange={e => setFormData({ ...formData, categoryId: e.target.value, subCategoryId: '' })}>
                  <option value="">Select Category</option>
                  {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>

              <div style={fieldStyle}>
                <label style={labelStyle}>Sub-Category</label>
                <select style={inputStyle} value={formData.subCategoryId}
                  onChange={e => setFormData({ ...formData, subCategoryId: e.target.value })}
                  disabled={!formData.categoryId}>
                  <option value="">Select Sub-Category</option>
                  {filteredSubCategories.map(sc => <option key={sc.id} value={sc.id}>{sc.name}</option>)}
                </select>
              </div>

              <div style={{ ...fieldStyle, gridColumn: 'span 2' }}>
                <label style={labelStyle}>Brand</label>
                <select style={inputStyle} value={formData.brandId}
                  onChange={e => setFormData({ ...formData, brandId: e.target.value })}>
                  <option value="">Select Brand (Optional)</option>
                  {brands.map(b => <option key={b.id} value={b.id}>{b.brandName}</option>)}
                </select>
              </div>

              {/* VARIANTS SECTION */}
              <div style={{ gridColumn: 'span 2', background: KM.bg, border: `1px solid ${KM.border}`, borderRadius: 8, padding: 14 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 15 }}>
                  <span style={{ ...labelStyle, color: KM.blue, fontWeight: 700 }}>Product Variants</span>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 1.5fr 1fr 1fr 1fr 32px', gap: 8, marginBottom: 8 }}>
                  <label style={labelStyle}>Unit</label>
                  <label style={labelStyle}>Name</label>
                  <label style={labelStyle}>MRP</label>
                  <label style={labelStyle}>Price</label>
                  <label style={labelStyle}>Stock</label>
                  <div />
                </div>

                {variants.map((v, index) => {
                  const isSize = SIZE_UNITS.includes(v.unit);
                  return (
                    <div key={index} style={{
                      display: 'grid',
                      gridTemplateColumns: isSize
                        ? '1.4fr 1fr 1fr 1fr 32px'
                        : '1.4fr 1.5fr 1fr 1fr 1fr 32px',
                      gap: 8, marginBottom: 8, alignItems: 'start'
                    }}>
                      <SmartUnitSelector
                        value={v.unit}
                        onChange={unit => updateVariant(index, 'unit', unit)}
                      />

                      {!isSize && (
                        <input
                          style={inputStyle}
                          value={v.variantName}
                          onChange={e => updateVariant(index, 'variantName', e.target.value)}
                          placeholder="500 Pack"
                          required
                        />
                      )}

                      <input style={inputStyle} type="number" value={v.mrp} onChange={e => updateVariant(index, 'mrp', e.target.value)} placeholder="0" required />
                      <input style={inputStyle} type="number" value={v.salesPrice} onChange={e => updateVariant(index, 'salesPrice', e.target.value)} placeholder="0" required />
                      <input style={inputStyle} type="number" value={v.stock} onChange={e => updateVariant(index, 'stock', e.target.value)} placeholder="0" required />
                      {variants.length > 1 && (
                        <button type="button" onClick={() => removeVariantField(index)} style={{ color: '#EF4444', border: 'none', background: 'none', cursor: 'pointer', paddingTop: 10 }}>✕</button>
                      )}
                    </div>
                  );
                })}
              </div>

              <div style={{ gridColumn: 'span 2', display: 'flex', gap: 20, padding: '12px 14px', background: KM.orangeLight, borderRadius: 8 }}>
                {[{ key: 'isNewArrival', label: 'New Arrival' }, { key: 'isRecommended', label: 'New Product' }, { key: 'isHotDeal', label: 'Hot Deal' }].map(item => (
                  <label key={item.key} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer' }}>
                    <input type="checkbox" checked={formData[item.key]} onChange={e => setFormData({ ...formData, [item.key]: e.target.checked })} />
                    {item.label}
                  </label>
                ))}
              </div>

              <div style={{ gridColumn: 'span 2', ...fieldStyle }}>
                <label style={labelStyle}>Product Image</label>
                <div style={{ border: `1.5px dashed ${KM.teal}`, borderRadius: 8, padding: 18, textAlign: 'center', background: '#F0FAFE', position: 'relative' }}>
                  <input type="file" accept="image/*" onChange={e => setImageFile(e.target.files[0])} style={{ opacity: 0, position: 'absolute', width: '100%', height: '100%', top: 0, left: 0, cursor: 'pointer' }} />
                  {imageFile ? <span style={{ color: KM.green, fontWeight: 600 }}>✓ {imageFile.name}</span> : <span style={{ color: KM.muted }}>Click to upload image</span>}
                </div>
              </div>

              <button type="submit" style={submitBtn}>{editingId ? 'Update Product' : 'Save Product'}</button>
            </form>
          </div>
        </div>
      )}

      {loading ? <p style={{ padding: 20 }}>Loading Products...</p> : (
        <DataTable
          columns={['ID', 'Image', 'Product', 'Brand', 'Price', 'Stock', 'Category / Sub', 'Actions']}
          initialRows={rows}
          renderRow={(row) => {
            const firstVariant = row.Variants?.[0];
            const totalStock = row.Variants?.reduce((acc, v) => acc + Number(v.stock || 0), 0) || 0;

            return (
              <tr key={row.id}>
                <td style={{ color: KM.muted }}>#{row.id}</td>

                <td>
                  {row.image && (
                    <img
                      src={`${API}/${row.image}`}
                      alt=""
                      width="40"
                      height="40"
                      style={{ borderRadius: 6, objectFit: 'cover' }}
                    />
                  )}
                </td>

                <td style={{ fontWeight: 600 }}>{row.productName}</td>

                <td style={{ fontSize: 12, color: KM.teal, fontWeight: 500 }}>
                  {row.Brand?.brandName || '—'}
                </td>

                <td style={{ fontWeight: 700 }}>₹{firstVariant?.salesPrice || 0}</td>

                <td>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div style={{
                      width: 8, height: 8, borderRadius: '50%',
                      background: totalStock === 0 ? '#dc2626' : stockColor(totalStock)
                    }} />
                    <span style={{ fontWeight: 700, color: totalStock === 0 ? '#dc2626' : KM.text }}>
                      {totalStock}
                    </span>
                  </div>
                </td>

                <td>
                  <div style={{ fontSize: 12, fontWeight: 500 }}>{row.Category?.name || 'N/A'}</div>
                  <div style={{ fontSize: 11, color: KM.teal }}>{row.SubCategory?.name || '—'}</div>
                </td>

                <td>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button className="action-btn btn-edit" onClick={() => handleEditClick(row)}>Edit</button>
                    <button className="action-btn btn-del" onClick={() => handleDelete(row.id)}>Delete</button>
                  </div>
                </td>
              </tr>
            );
          }}
        />
      )}
    </div>
  );
}